import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, ImagePlus, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    barcode: '',
    brand: '',
    category: '',
    subcategory: '',
    description: '',
    purchase_price: '',
    price: '',
    promo_price: '',
    stock: '',
    weight: '',
    dimensions: '',
    color: '',
    variant: '',
    tags: '',
    image_url: '',
    additional_images: [],
    video_url: '',
    pdf_url: '',
    is_available: true,
    is_featured: false,
    is_new: true,
    is_recommended: false,
    meta_title: '',
    meta_description: '',
    custom_url: ''
  });

  useEffect(() => {
    fetchCategories();
    fetchBrands();
    if (isEditing) {
      fetchProduct();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`, { headers: getAuthHeader() });
      setCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const res = await axios.get(`${API}/brands`, { headers: getAuthHeader() });
      setBrands(res.data);
    } catch (error) {
      console.error('Error fetching brands:', error);
    }
  };

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/products/${id}`, { headers: getAuthHeader() });
      const product = res.data;
      setFormData({
        name: product.name || '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        brand: product.brand || '',
        category: product.category || '',
        subcategory: product.subcategory || '',
        description: product.description || '',
        purchase_price: product.purchase_price?.toString() || '',
        price: product.price?.toString() || '',
        promo_price: product.promo_price?.toString() || '',
        stock: product.stock?.toString() || '',
        weight: product.weight || '',
        dimensions: product.dimensions || '',
        color: product.color || '',
        variant: product.variant || '',
        tags: product.tags || '',
        image_url: product.image_url || '',
        additional_images: product.additional_images || [],
        video_url: product.video_url || '',
        pdf_url: product.pdf_url || '',
        is_available: product.is_available !== false,
        is_featured: product.is_featured || false,
        is_new: product.is_new || false,
        is_recommended: product.is_recommended || false,
        meta_title: product.meta_title || '',
        meta_description: product.meta_description || '',
        custom_url: product.custom_url || ''
      });
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Erè pandan chaje pwodwi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        promo_price: formData.promo_price ? parseFloat(formData.promo_price) : null,
        stock: parseInt(formData.stock) || 0
      };

      if (isEditing) {
        await axios.put(`${API}/products/${id}`, productData, {
          headers: getAuthHeader()
        });
        toast.success('Pwodwi mete ajou!');
      } else {
        await axios.post(`${API}/products`, productData, {
          headers: getAuthHeader()
        });
        toast.success('Pwodwi kreye!');
      }

      navigate('/admin/products');
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Erè pandan sove pwodwi');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addImageUrl = () => {
    const url = prompt('Antre URL imaj la:');
    if (url) {
      setFormData(prev => ({
        ...prev,
        additional_images: [...prev.additional_images, url]
      }));
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_images: prev.additional_images.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#001A3E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-product-form">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/admin/products')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-[#001A3E]">
            {isEditing ? 'Modifye Pwodwi' : 'Nouvo Pwodwi'}
          </h1>
          <p className="text-gray-500">
            {isEditing ? 'Mete ajou enfòmasyon pwodwi a' : 'Ajoute yon nouvo pwodwi'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle>Enfòmasyon Debaz</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="name">Non Pwodwi *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required
                      placeholder="Egzanp: iPhone 15 Pro"
                    />
                  </div>
                  <div>
                    <Label htmlFor="sku">SKU</Label>
                    <Input
                      id="sku"
                      value={formData.sku}
                      onChange={(e) => handleChange('sku', e.target.value)}
                      placeholder="SKU-001"
                    />
                  </div>
                  <div>
                    <Label htmlFor="barcode">Barcode</Label>
                    <Input
                      id="barcode"
                      value={formData.barcode}
                      onChange={(e) => handleChange('barcode', e.target.value)}
                      placeholder="123456789"
                    />
                  </div>
                  <div>
                    <Label htmlFor="brand">Brand</Label>
                    <Select value={formData.brand} onValueChange={(v) => handleChange('brand', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Chwazi brand" />
                      </SelectTrigger>
                      <SelectContent>
                        {brands.map((brand) => (
                          <SelectItem key={brand.id} value={brand.name}>
                            {brand.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="category">Kategori *</Label>
                    <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Chwazi kategori" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.name}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">Deskripsyon</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    rows={4}
                    placeholder="Dekri pwodwi a..."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card>
              <CardHeader>
                <CardTitle>Pri ak Stock</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="purchase_price">Pri Acha ($)</Label>
                    <Input
                      id="purchase_price"
                      type="number"
                      step="0.01"
                      value={formData.purchase_price}
                      onChange={(e) => handleChange('purchase_price', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="price">Pri Vant ($) *</Label>
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => handleChange('price', e.target.value)}
                      required
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="promo_price">Pri Promo ($)</Label>
                    <Input
                      id="promo_price"
                      type="number"
                      step="0.01"
                      value={formData.promo_price}
                      onChange={(e) => handleChange('promo_price', e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="stock">Stock *</Label>
                    <Input
                      id="stock"
                      type="number"
                      value={formData.stock}
                      onChange={(e) => handleChange('stock', e.target.value)}
                      required
                      placeholder="0"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Details */}
            <Card>
              <CardHeader>
                <CardTitle>Detay Pwodwi</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="weight">Pwa</Label>
                    <Input
                      id="weight"
                      value={formData.weight}
                      onChange={(e) => handleChange('weight', e.target.value)}
                      placeholder="1kg"
                    />
                  </div>
                  <div>
                    <Label htmlFor="dimensions">Dimansyon</Label>
                    <Input
                      id="dimensions"
                      value={formData.dimensions}
                      onChange={(e) => handleChange('dimensions', e.target.value)}
                      placeholder="10x10x10"
                    />
                  </div>
                  <div>
                    <Label htmlFor="color">Koulè</Label>
                    <Input
                      id="color"
                      value={formData.color}
                      onChange={(e) => handleChange('color', e.target.value)}
                      placeholder="Nwa, Blan..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="variant">Variant</Label>
                    <Input
                      id="variant"
                      value={formData.variant}
                      onChange={(e) => handleChange('variant', e.target.value)}
                      placeholder="S, M, L, XL"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="tags">Tags</Label>
                  <Input
                    id="tags"
                    value={formData.tags}
                    onChange={(e) => handleChange('tags', e.target.value)}
                    placeholder="nouvo, popilè, promo (separe ak virgule)"
                  />
                </div>
              </CardContent>
            </Card>

            {/* SEO */}
            <Card>
              <CardHeader>
                <CardTitle>SEO</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="meta_title">Meta Title</Label>
                  <Input
                    id="meta_title"
                    value={formData.meta_title}
                    onChange={(e) => handleChange('meta_title', e.target.value)}
                    placeholder="Tit pou motè rechèch"
                  />
                </div>
                <div>
                  <Label htmlFor="meta_description">Meta Description</Label>
                  <Textarea
                    id="meta_description"
                    value={formData.meta_description}
                    onChange={(e) => handleChange('meta_description', e.target.value)}
                    rows={2}
                    placeholder="Deskripsyon pou motè rechèch"
                  />
                </div>
                <div>
                  <Label htmlFor="custom_url">URL Pèsonalize</Label>
                  <Input
                    id="custom_url"
                    value={formData.custom_url}
                    onChange={(e) => handleChange('custom_url', e.target.value)}
                    placeholder="pwodwi-mwen"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <Card>
              <CardHeader>
                <CardTitle>Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="is_available">Disponib</Label>
                  <Switch
                    id="is_available"
                    checked={formData.is_available}
                    onCheckedChange={(v) => handleChange('is_available', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="is_featured">Vedèt</Label>
                  <Switch
                    id="is_featured"
                    checked={formData.is_featured}
                    onCheckedChange={(v) => handleChange('is_featured', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="is_new">Nouvo</Label>
                  <Switch
                    id="is_new"
                    checked={formData.is_new}
                    onCheckedChange={(v) => handleChange('is_new', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="is_recommended">Rekòmande</Label>
                  <Switch
                    id="is_recommended"
                    checked={formData.is_recommended}
                    onCheckedChange={(v) => handleChange('is_recommended', v)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Main Image */}
            <Card>
              <CardHeader>
                <CardTitle>Imaj Prensipal</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="image_url">URL Imaj *</Label>
                  <Input
                    id="image_url"
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => handleChange('image_url', e.target.value)}
                    required
                    placeholder="https://..."
                  />
                </div>
                {formData.image_url && (
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="w-full h-40 object-cover rounded"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
              </CardContent>
            </Card>

            {/* Additional Images */}
            <Card>
              <CardHeader>
                <CardTitle>Imaj Adisyonèl</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button type="button" variant="outline" className="w-full" onClick={addImageUrl}>
                  <ImagePlus className="mr-2 h-4 w-4" />
                  Ajoute Imaj
                </Button>
                <div className="grid grid-cols-3 gap-2">
                  {formData.additional_images.map((img, index) => (
                    <div key={index} className="relative">
                      <img
                        src={img}
                        alt={`Additional ${index}`}
                        className="w-full h-16 object-cover rounded"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Media */}
            <Card>
              <CardHeader>
                <CardTitle>Medya</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="video_url">URL Videyo</Label>
                  <Input
                    id="video_url"
                    type="url"
                    value={formData.video_url}
                    onChange={(e) => handleChange('video_url', e.target.value)}
                    placeholder="https://youtube.com/..."
                  />
                </div>
                <div>
                  <Label htmlFor="pdf_url">URL PDF</Label>
                  <Input
                    id="pdf_url"
                    type="url"
                    value={formData.pdf_url}
                    onChange={(e) => handleChange('pdf_url', e.target.value)}
                    placeholder="https://..."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Save Button */}
            <Button 
              type="submit" 
              className="w-full bg-[#001A3E] hover:bg-[#001A3E]/90"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Ap Sove...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {isEditing ? 'Mete Ajou' : 'Kreye Pwodwi'}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
